import React, { useCallback, useState } from 'react';
import { GoogleMap, useJsApiLoader, MarkerF, InfoWindowF } from '@react-google-maps/api';

const STATUS_COLORS = {
  Available: '#22c55e',
  Charging: '#1f9a4c',
  Offline: '#94a3b8',
  Faulted: '#ef4444',
};

function getStationColor(station) {
  if (station.faulted_count > 0) return STATUS_COLORS.Faulted;
  if (station.charging_count > 0) return STATUS_COLORS.Charging;
  if (station.available_count > 0) return STATUS_COLORS.Available;
  return STATUS_COLORS.Offline;
}

// Default center when there are no stations — Aberdeen, UK
const DEFAULT_CENTER = { lat: 57.1497, lng: -2.0943 };
const DEFAULT_ZOOM = 12;

const mapContainerStyle = { width: '100%', height: '100%', borderRadius: 'inherit' };

// A simple, custom pin-shaped SVG icon, colored per station status
function pinIcon(color) {
  const svg = `
    <svg width="34" height="42" viewBox="0 0 34 42" xmlns="http://www.w3.org/2000/svg">
      <path d="M17 0C7.6 0 0 7.6 0 17c0 12.75 17 25 17 25s17-12.25 17-25C34 7.6 26.4 0 17 0z" fill="${color}" stroke="#fff" stroke-width="2"/>
      <circle cx="17" cy="17" r="7" fill="#fff"/>
      <path d="M18.5 10.5l-6 8h4l-1.5 7 6-8h-4l1.5-7z" fill="${color}"/>
    </svg>
  `;
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: window.google ? new window.google.maps.Size(34, 42) : undefined,
    anchor: window.google ? new window.google.maps.Point(17, 42) : undefined,
  };
}

export default function NetworkMap({ stations, onStationClick }) {
  const { isLoaded } = useJsApiLoader({
    id: 'srlk-google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_KEY,
  });

  const [activeStation, setActiveStation] = useState(null);

  const validStations = stations.filter(s => s.latitude && s.longitude && !isNaN(parseFloat(s.latitude)) && !isNaN(parseFloat(s.longitude)));
  const hasStations = validStations.length > 0;

  const avgLat = hasStations
    ? validStations.reduce((sum, s) => sum + parseFloat(s.latitude), 0) / validStations.length
    : DEFAULT_CENTER.lat;
  const avgLng = hasStations
    ? validStations.reduce((sum, s) => sum + parseFloat(s.longitude), 0) / validStations.length
    : DEFAULT_CENTER.lng;

  const onLoad = useCallback((map) => {
    if (hasStations && validStations.length > 1) {
      const bounds = new window.google.maps.LatLngBounds();
      validStations.forEach(s => bounds.extend({ lat: parseFloat(s.latitude), lng: parseFloat(s.longitude) }));
      map.fitBounds(bounds);
    }
  }, [hasStations, validStations]);

  if (!isLoaded) {
    return (
      <div className="network-map-wrap" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--gray-25)' }}>
        <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading map...</span>
      </div>
    );
  }

  return (
    <div className="network-map-wrap">
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={{ lat: avgLat, lng: avgLng }}
        zoom={hasStations ? (validStations.length === 1 ? 14 : 11) : DEFAULT_ZOOM}
        onLoad={onLoad}
        options={{
          scrollwheel: false,
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: true,
        }}
      >
        {validStations.map(station => (
          <MarkerF
            key={station.id}
            position={{ lat: parseFloat(station.latitude), lng: parseFloat(station.longitude) }}
            icon={pinIcon(getStationColor(station))}
            onClick={() => setActiveStation(station)}
          />
        ))}

        {activeStation && (
          <InfoWindowF
            position={{ lat: parseFloat(activeStation.latitude), lng: parseFloat(activeStation.longitude) }}
            onCloseClick={() => setActiveStation(null)}
          >
            <div style={{ fontFamily: 'inherit', minWidth: 160 }}>
              <div style={{ fontWeight: 700, fontSize: 13.5, marginBottom: 4 }}>{activeStation.station_name}</div>
              <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>{activeStation.organisation_name}</div>
              <div style={{ display: 'flex', gap: 10, fontSize: 12, marginBottom: 8 }}>
                <span style={{ color: '#22c55e' }}>{activeStation.available_count || 0} available</span>
                <span style={{ color: '#1f9a4c' }}>{activeStation.charging_count || 0} charging</span>
                <span style={{ color: '#94a3b8' }}>{activeStation.offline_count || 0} offline</span>
              </div>
              {onStationClick && (
                <button
                  onClick={() => onStationClick(activeStation)}
                  style={{ fontSize: 12, color: '#1f9a4c', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                >
                  View station →
                </button>
              )}
            </div>
          </InfoWindowF>
        )}
      </GoogleMap>
    </div>
  );
}